import type { ThemeId } from '@vitvio/design-system';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
  Text,
  Flex,
  FormControl,
  FormHelperText,
  FormErrorMessage,
  Input,
  Kbd,
  useTheme,
  useThemeContext,
} from '@vitvio/design-system';
import { useUserContext } from '@vitvio/user-context';
import { useState, useEffect } from 'react';
import { getCurrentLocale, setLocale, useTranslation, locales } from '@vitvio/locales';
import { getDifference } from '@vitvio/helpers';
import { useBuildMetadata } from '@vitvio/build-metadata';
import { useForm } from 'react-hook-form';
import { useDemoFunctionalityContext } from '@vitvio/demo-functionality';
import { getLoggerSafe } from '@vitvio/logging';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MIN_ROOMS = 3;
const MAX_ROOMS = 15;
const DEMO_PHONE_NUMBERS_KEY = 'demoPhoneNumbers';
const DEMO_PHONE_EXPIRY_TIME_KEY = 'demoPhoneNumberExpiryTime';

// Local functions to avoid circular dependencies
function validateDemoPhoneInput(input: string): boolean {
  return /^[\d,+\s]*$/g.test(input);
}

function getPhoneExpiryTime() {
  const expiryTimeRaw = localStorage.getItem(DEMO_PHONE_EXPIRY_TIME_KEY);

  if (!expiryTimeRaw) {
    return 60;
  }

  try {
    return JSON.parse(expiryTimeRaw);
  } catch (e) {
    localStorage.removeItem(DEMO_PHONE_EXPIRY_TIME_KEY);
    return 60;
  }
}

function getDemoPhoneNumbers() {
  const logger = getLoggerSafe();
  const phoneNumbersRaw = localStorage.getItem(DEMO_PHONE_NUMBERS_KEY);
  const expiryTime = getPhoneExpiryTime();

  if (!phoneNumbersRaw) {
    return null;
  }

  try {
    const phoneNumbers = JSON.parse(phoneNumbersRaw);

    const difference = getDifference(phoneNumbers.savedAt, new Date(), 'minutes');
    const isExpired = difference >= expiryTime;

    if (isExpired) {
      localStorage.removeItem(DEMO_PHONE_NUMBERS_KEY);
      return null;
    }

    return phoneNumbers.numbers;
  } catch (e) {
    logger?.error(e as Error, { context: 'getDemoPhoneNumbers' });
    localStorage.removeItem(DEMO_PHONE_NUMBERS_KEY);
    return null;
  }
}

export function UserSettingsModal({ isOpen, onClose }: UserSettingsModalProps) {
  const { activeHospital, availableHospitals, timeFormat, updateTimeFormat, changeActiveHospital } = useUserContext();
  const [selectedHospital, setSelectedHospital] = useState(activeHospital.id);
  const { t } = useTranslation();
  const { getColor, getSpacing } = useTheme();
  const { availableThemes, currentTheme, setTheme } = useThemeContext();

  const {
    numberOfOperatingRooms,
    setNumberOfOperatingRooms,
    statusMessage,
    isInDemoMode,
    setDemoTimeZone,
    demoTimeZone,
    createDebugDataForToday,
  } = useDemoFunctionalityContext();

  // Build metadata
  const { version, environment, branch } = useBuildMetadata();

  // Phone numbers state
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset: resetForm,
  } = useForm({ defaultValues: { expiryTime: getPhoneExpiryTime() } });

  // Sync selectedHospital when activeHospital changes externally
  useEffect(() => {
    setSelectedHospital(activeHospital.id);
  }, [activeHospital.id]);

  function handleTimeFormatChange(e: React.ChangeEvent<HTMLSelectElement>) {
    if (e.target.value === '12hr' || e.target.value === '24hr') {
      updateTimeFormat(e.target.value);
    }
  }

  function handleHospitalChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setSelectedHospital(e.target.value);
  }

  function handleHospitalSubmit() {
    changeActiveHospital(selectedHospital);
  }

  // Demo timezone handler
  function handleDemoTimezoneChange(timezone: string) {
    setDemoTimeZone(timezone);
  }

  // Phone number handlers
  useEffect(() => {
    const phoneNumbers = getDemoPhoneNumbers();
    if (!phoneNumbers) {
      return;
    }
    setPhoneNumbers(phoneNumbers);
  }, []);

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setPhoneInput(e.target.value);

  const isPhoneError = phoneInput.length > 0 && !validateDemoPhoneInput(phoneInput);

  function savePhoneNumbers() {
    if (isPhoneError) {
      return;
    }

    const numbers = phoneInput.split(',').map((n) => n.trim());

    setPhoneNumbers(numbers);

    localStorage.setItem(DEMO_PHONE_NUMBERS_KEY, JSON.stringify({ savedAt: new Date(), numbers }));

    setPhoneInput('');
  }

  function clearPhoneNumbers() {
    localStorage.removeItem(DEMO_PHONE_NUMBERS_KEY);
    setPhoneNumbers(undefined);
  }

  function saveExpiryTime({ expiryTime }: { expiryTime: number }) {
    if (errors.expiryTime) {
      return;
    }
    resetForm({ expiryTime });
    localStorage.setItem(DEMO_PHONE_EXPIRY_TIME_KEY, JSON.stringify(expiryTime));
  }

  function handleEnterKeyNumbersInput(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      savePhoneNumbers();
    }
  }

  function handleEnterKeyExpiryInput(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      handleSubmit(saveExpiryTime)();
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={'xl'}>
      <ModalOverlay />
      <ModalContent maxW={'640px'} w={'640px'} bg={getColor('bg.overlay')}>
        <ModalHeader color={getColor('fg')}>{t('common:settingsMenu.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text font="headings.XXS_SemiBold" path="common:settingsMenu.section.globalSettings" />
          <Flex gap={getSpacing('5')} mt={getSpacing('7')}>
            <Box flex="0 0 376px">
              <Box mb={getSpacing('4')}>
                <Text mb={getSpacing('2')} path="common:settingsMenu.language.label" />
                <Select
                  color={getColor('fg.muted.constant')}
                  borderColor={getColor('border')}
                  defaultValue={getCurrentLocale()}
                  onChange={(e) => setLocale(e.target.value)}
                >
                  {locales.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box mb={getSpacing('4')}>
                <Text mb={getSpacing('2')} path="common:settingsMenu.operatingRooms.label" />
                <Select
                  color={getColor('fg.muted.constant')}
                  borderColor={getColor('border')}
                  defaultValue={numberOfOperatingRooms.toString()}
                  onChange={(e) => setNumberOfOperatingRooms(Number(e.target.value))}
                >
                  {Array.from({ length: MAX_ROOMS - MIN_ROOMS + 1 }, (_, i) => i + MIN_ROOMS).map((number) => (
                    <option key={number} value={number}>
                      {t('common:settingsMenu.operatingRooms.value', { number })}
                    </option>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box flex="0 0 200px">
              <Box mb={getSpacing('4')}>
                <Text mb={getSpacing('2')} path="common:settingsMenu.theme.label" />
                <Select
                  color={getColor('fg.muted.constant')}
                  borderColor={getColor('border')}
                  defaultValue={currentTheme}
                  onChange={(e) => setTheme(e.target.value as ThemeId)}
                >
                  {availableThemes.map((id) => (
                    <option key={id} value={id}>
                      {t(`common:settingsMenu.theme.name.${id}`, { defaultValue: id })}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box mb={getSpacing('4')}>
                <Text mb={getSpacing('2')} path="common:settingsMenu.timeFormat.label" />
                <Select
                  color={getColor('fg.muted.constant')}
                  borderColor={getColor('border')}
                  defaultValue={timeFormat}
                  onChange={handleTimeFormatChange}
                >
                  <option value="12hr">{t('common:settingsMenu.timeFormat.12hr')}</option>
                  <option value="24hr">{t('common:settingsMenu.timeFormat.24hr')}</option>
                </Select>
              </Box>
            </Box>
          </Flex>
          <Box mb={getSpacing('5')}>
            <Text mb={getSpacing('2')} path="common:settingsMenu.hospital.label" />
            <Select
              color={getColor('fg.muted.constant')}
              borderColor={getColor('border')}
              value={selectedHospital}
              onChange={handleHospitalChange}
            >
              {availableHospitals &&
                availableHospitals.map((hospital) => (
                  <option key={hospital?.id} value={hospital?.id || 'missing'}>
                    {hospital?.name}
                  </option>
                ))}
            </Select>
          </Box>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
            <Text path="common:settingsMenu.hospital.timezone.label" />
            <Text fontWeight={500}>
              {availableHospitals?.find((hospital) => hospital?.id === selectedHospital)?.timezone}
            </Text>
          </div>
          <Text
            font="headings.XXS_SemiBold"
            path="common:settingsMenu.section.debugSettings"
            mt={getSpacing('7')}
            mb={getSpacing('5')}
          />
          <Flex justifyContent={'space-between'} alignItems={'center'} mb={getSpacing('4')}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Text path="common:debugSettings.demoMode">:&nbsp;</Text>
              <Text path={isInDemoMode ? 'common:status.on' : 'common:status.off'} />
            </div>
          </Flex>
          <Box my={getSpacing('4')}>
            <Text path="common:debugSettings.timezone.label" />
            <Select
              color={getColor('fg.muted.constant')}
              borderColor={getColor('border')}
              value={demoTimeZone}
              onChange={(e) => handleDemoTimezoneChange(e.target.value)}
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York</option>
              <option value="America/Los_Angeles">America/Los_Angeles</option>
              <option value="Europe/London">Europe/London</option>
              <option value="Asia/Tokyo">Asia/Tokyo</option>
            </Select>
          </Box>
          <Box>
            <FormControl>
              <Text path="common:debugSettings.phoneNumbers.label" />
              <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                <Input
                  color={getColor('fg.muted.constant')}
                  borderColor={getColor('border')}
                  value={phoneInput}
                  onChange={handlePhoneInputChange}
                  onKeyDown={handleEnterKeyNumbersInput}
                />
                <Button onClick={savePhoneNumbers} bg={getColor('button.bg.secondary')} color={getColor('fg')}>
                  {t('common:debugSettings.phoneNumbers.save')}
                </Button>
              </div>

              {isPhoneError ? (
                <FormHelperText color="red.500">{t('common:debugSettings.phoneNumbers.error')}</FormHelperText>
              ) : (
                <Flex gap={getSpacing('2')}>
                  <FormHelperText>{t('common:debugSettings.phoneNumbers.placeholder')}</FormHelperText>
                  {phoneInput && (
                    <FormHelperText>
                      {t('common:debugSettings.phoneNumbers.enterToSave')} <Kbd>Enter</Kbd>
                    </FormHelperText>
                  )}
                </Flex>
              )}
            </FormControl>
            <div style={{ marginTop: '16px' }}>
              <FormControl as={'form'} onSubmit={handleSubmit(saveExpiryTime)} isInvalid={Boolean(errors?.expiryTime)}>
                <Text path="common:debugSettings.phoneExpiry.label" />
                <Flex gap={getSpacing('2')}>
                  <div style={{ display: 'flex', flex: 1, flexDirection: 'row', gap: getSpacing('2') }}>
                    <Input
                      type="number"
                      borderColor={getColor('border')}
                      color={getColor('fg.muted.constant')}
                      {...register('expiryTime', {
                        valueAsNumber: true,
                        min: { value: 1, message: t('common:debugSettings.phoneExpiry.minError') },
                        max: {
                          value: 60 * 24,
                          message: t('common:debugSettings.phoneExpiry.maxError'),
                        },
                      })}
                      placeholder={t('common:debugSettings.phoneExpiry.placeholder')}
                      onKeyDown={handleEnterKeyExpiryInput}
                    />

                    <Button type="submit" bg={getColor('button.bg.secondary')} color={getColor('fg')}>
                      {t('common:debugSettings.phoneExpiry.save')}
                    </Button>
                  </div>
                </Flex>
                <FormErrorMessage>{`${errors.expiryTime && errors.expiryTime.message}`}</FormErrorMessage>
                {isDirty && (
                  <FormHelperText>
                    {t('common:debugSettings.phoneExpiry.enterToSave')} <Kbd>Enter</Kbd>
                  </FormHelperText>
                )}
              </FormControl>
            </div>
            {phoneNumbers && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <Text path="common:debugSettings.phoneNumbers.currentlySaved" />
                  <Text fontWeight={500}>{phoneNumbers.join(', ')}</Text>
                </div>
                {!!phoneNumbers?.length && (
                  <Button
                    mt={getSpacing('3')}
                    onClick={clearPhoneNumbers}
                    bg={getColor('button.bg.secondary')}
                    color={getColor('fg')}
                  >
                    {t('common:debugSettings.phoneNumbers.clear')}
                  </Button>
                )}
              </div>
            )}
          </Box>
          <Text style={{ marginTop: getSpacing('4') }} color={getColor('fg.muted.constant')}>
            {t('common:debugSettings.version', { branch, version, environment })}
          </Text>
        </ModalBody>
        <ModalFooter gap={getSpacing('5')}>
          <Button bg={getColor('button.bg.primary')} color={getColor('fg.inverted')} onClick={handleHospitalSubmit}>
            {t('common:settingsMenu.submit')}
          </Button>
          <Button
            bg={getColor('button.bg.primary.subtle')}
            color={getColor('fg')}
            onClick={createDebugDataForToday}
            isDisabled={!!statusMessage}
            loadingText={t('common:debugSettings.resetDemo.loading')}
            isLoading={!!statusMessage}
            mr="auto"
          >
            {t('common:debugSettings.resetDemo.button')}
          </Button>
          <Button bg={getColor('button.bg.secondary')} color={getColor('fg')} onClick={onClose}>
            {t('common:settingsMenu.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
